import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import useGameSocket from "@/lib/useGameSocket";
import GameInviteModal from "@/components/GameInviteModal";
import { Users, Wifi, Copy, ArrowLeft, Eye } from "lucide-react";

/**
 * Neon Cycles — Tron-style light-cycle duel.
 *   Grid: 80 × 48 cells (10px each → 800×480 canvas)
 *   Host = cyan cycle (starts left, moving right)
 *   Guest = pink cycle (starts right, moving left)
 *
 * Modes: LOCAL (WASD vs ↑/↓/←/→), HOST, GUEST, SPECTATOR.
 * Host is authoritative — runs grid ticks + collision detection at ~12Hz and
 * broadcasts full state on every tick. Guest only submits its intended
 * direction. Best of 5 rounds.
 */

const COLS = 80;
const ROWS = 48;
const CELL = 10;            // px per cell in the rendered canvas
const TICK_MS = 78;         // grid step ≈ 12.8 Hz
const ROUNDS_TO_WIN = 3;

// Direction constants stored as unit deltas (dx, dy).
const DIRS = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
const opposite = (a, b) => a[0] + b[0] === 0 && a[1] + b[1] === 0;

function initialRound() {
  return {
    tick: 0,
    // Host cycle
    hX: 8, hY: Math.floor(ROWS / 2), hDir: [1, 0], hAlive: true, hTrail: new Set(),
    // Guest cycle
    gX: COLS - 9, gY: Math.floor(ROWS / 2), gDir: [-1, 0], gAlive: true, gTrail: new Set(),
    winner: null,   // "L" (host), "R" (guest), or "DRAW"
  };
}

const cellKey = (x, y) => y * COLS + x;

export default function NeonCycles({ joinRoomId = null, spectate = false }) {
  const [stage, setStage] = useState(joinRoomId ? "waiting" : "start");
  const [mode, setMode] = useState(joinRoomId ? (spectate ? "spectator" : "guest") : null);
  const [roomId, setRoomId] = useState(joinRoomId);
  const [peerName, setPeerName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [error, setError] = useState("");
  const [specCount, setSpecCount] = useState(0);
  const [score, setScore] = useState({ l: 0, r: 0 });
  const [matchOver, setMatchOver] = useState(null); // "L" | "R" | null

  const canvasRef = useRef(null);
  const roundRef = useRef(initialRound());
  const nextHostDirRef = useRef([1, 0]);
  const nextGuestDirRef = useRef([-1, 0]);
  const lastTickRef = useRef(0);
  const scoreRef = useRef({ l: 0, r: 0 });
  const roundResolvedRef = useRef(false);   // avoid double-counting deaths within a tick
  const rafRef = useRef(0);

  // Guest-only latest server state
  const guestSnapRef = useRef(null);

  // ---------------- Networking ----------------
  const onNetMessage = useCallback((m) => {
    if (m.type === "peer_joined") {
      if (typeof m.spectator_count === "number") setSpecCount(m.spectator_count);
      if (m.side !== "spectator" && m.username) setPeerName(prev => prev || m.username);
      if (mode === "host" && m.side === "guest") setStage("playing");
    } else if (m.type === "peer_left") {
      if (typeof m.spectator_count === "number") setSpecCount(m.spectator_count);
      if (m.side !== "spectator") {
        setError(`${m.username || "Peer"} disconnected`);
      }
    } else if (m.type === "input" && mode === "host") {
      // Guest → intended direction as "up"/"down"/"left"/"right"
      const dir = DIRS[m.dir];
      if (dir && !opposite(roundRef.current.gDir, dir)) {
        nextGuestDirRef.current = dir;
      }
    } else if (m.type === "state" && (mode === "guest" || mode === "spectator")) {
      // Received authoritative snapshot; store for the render loop
      guestSnapRef.current = m;
      if (typeof m.ls === "number" && typeof m.rs === "number") {
        setScore({ l: m.ls, r: m.rs });
      }
      if (m.matchWinner) setMatchOver(m.matchWinner);
    } else if (m.type === "restart") {
      // Reset both refs + state for a fresh match
      roundRef.current = initialRound();
      nextHostDirRef.current = [1, 0];
      nextGuestDirRef.current = [-1, 0];
      scoreRef.current = { l: 0, r: 0 };
      setScore({ l: 0, r: 0 });
      setMatchOver(null);
      roundResolvedRef.current = false;
    }
  }, [mode]);

  const socket = useGameSocket({
    roomId,
    enabled: mode === "host" || mode === "guest" || mode === "spectator",
    onMessage: onNetMessage,
  });

  // Auto-transition helpers
  useEffect(() => {
    if ((mode === "guest" || mode === "spectator") && socket.connected && stage === "waiting") {
      setStage("playing");
    }
  }, [mode, socket.connected, stage]);

  useEffect(() => {
    if (socket.hello) {
      if (typeof socket.hello.spectator_count === "number") setSpecCount(socket.hello.spectator_count);
      const other = socket.hello.side === "host" ? socket.hello.guest : socket.hello.host;
      if (other) setPeerName(other);
      // Spectator arriving mid-match → auto-observe
      if (socket.hello.side === "spectator") setMode("spectator");
    }
  }, [socket.hello]);

  // ---------------- Mode-start helpers ----------------
  const startLocal = () => { setMode("local"); setStage("playing"); resetMatch(); };
  const startOnline = async () => {
    setError("");
    try {
      const r = await api.post("/games/rooms", { slug: "tron" });
      const rid = r.data?.room?.id;
      if (!rid) throw new Error("no room id");
      setRoomId(rid); setMode("host"); setStage("waiting"); setInviteOpen(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't create room");
    }
  };
  const cancelOnline = () => {
    socket.close();
    setMode(null); setRoomId(null); setStage("start"); setInviteOpen(false); setError("");
  };
  const resetMatch = () => {
    roundRef.current = initialRound();
    nextHostDirRef.current = [1, 0];
    nextGuestDirRef.current = [-1, 0];
    scoreRef.current = { l: 0, r: 0 };
    setScore({ l: 0, r: 0 });
    setMatchOver(null);
    roundResolvedRef.current = false;
  };
  const restartMatch = () => {
    resetMatch();
    if (mode === "host" || mode === "guest") socket.send({ type: "restart" });
  };
  const startNextRound = () => {
    // Preserve scores, reset positions/trails
    const rnd = initialRound();
    // Alternate starting direction slightly to keep it interesting
    rnd.tick = roundRef.current.tick + 1;
    roundRef.current = rnd;
    nextHostDirRef.current = [1, 0];
    nextGuestDirRef.current = [-1, 0];
    roundResolvedRef.current = false;
  };

  // Guest: REST-join before opening the socket
  useEffect(() => {
    if (mode === "guest" && roomId && !joinRoomId) return; // covered by joinRoomId path
    if (mode === "guest" && roomId) {
      api.post(`/games/rooms/${roomId}/join`).catch((e) => {
        const status = e?.response?.status;
        // 409 means room is full → we're spectating.
        if (status === 409) {
          setMode("spectator");
        } else {
          setError(e?.response?.data?.detail || "Couldn't join room");
        }
      });
    }
  }, [mode, roomId, joinRoomId]);

  // ---------------- Keyboard input ----------------
  useEffect(() => {
    if (stage !== "playing") return;
    if (mode === "spectator") return;

    const handle = (e) => {
      const k = e.key;
      let localDir = null, remoteDirKey = null;

      // Local mode: WASD = host, arrows = guest
      if (mode === "local") {
        if (k === "w" || k === "W") { localDir = "host-up"; }
        else if (k === "s" || k === "S") { localDir = "host-down"; }
        else if (k === "a" || k === "A") { localDir = "host-left"; }
        else if (k === "d" || k === "D") { localDir = "host-right"; }
        else if (k === "ArrowUp") { localDir = "guest-up"; e.preventDefault(); }
        else if (k === "ArrowDown") { localDir = "guest-down"; e.preventDefault(); }
        else if (k === "ArrowLeft") { localDir = "guest-left"; e.preventDefault(); }
        else if (k === "ArrowRight") { localDir = "guest-right"; e.preventDefault(); }
      } else {
        // Networked: either WASD or arrows → OWN direction
        if (k === "w" || k === "W" || k === "ArrowUp") { remoteDirKey = "up"; e.preventDefault(); }
        else if (k === "s" || k === "S" || k === "ArrowDown") { remoteDirKey = "down"; e.preventDefault(); }
        else if (k === "a" || k === "A" || k === "ArrowLeft") { remoteDirKey = "left"; e.preventDefault(); }
        else if (k === "d" || k === "D" || k === "ArrowRight") { remoteDirKey = "right"; e.preventDefault(); }
      }

      if (localDir) {
        const [who, dirName] = localDir.split("-");
        const nd = DIRS[dirName];
        if (nd) {
          if (who === "host" && !opposite(roundRef.current.hDir, nd)) nextHostDirRef.current = nd;
          if (who === "guest" && !opposite(roundRef.current.gDir, nd)) nextGuestDirRef.current = nd;
        }
      } else if (remoteDirKey) {
        const nd = DIRS[remoteDirKey];
        if (!nd) return;
        if (mode === "host") {
          if (!opposite(roundRef.current.hDir, nd)) nextHostDirRef.current = nd;
        } else if (mode === "guest") {
          // Guest sends intent; host authoritatively applies.
          socket.send({ type: "input", dir: remoteDirKey });
        }
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [stage, mode, socket]);

  // ---------------- Main render/physics loop ----------------
  useEffect(() => {
    if (stage !== "playing") return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");

    const tick = (now) => {
      // ----- Authoritative simulation (host / local only) -----
      if ((mode === "local" || mode === "host") && !matchOver) {
        if (now - lastTickRef.current >= TICK_MS) {
          lastTickRef.current = now;
          const r = roundRef.current;
          if (r.hAlive && r.gAlive) {
            // Apply queued directions
            r.hDir = nextHostDirRef.current;
            r.gDir = nextGuestDirRef.current;
            // Lay down current cell to trail BEFORE moving
            r.hTrail.add(cellKey(r.hX, r.hY));
            r.gTrail.add(cellKey(r.gX, r.gY));
            // Advance heads
            r.hX += r.hDir[0]; r.hY += r.hDir[1];
            r.gX += r.gDir[0]; r.gY += r.gDir[1];
            // Collision checks
            const hitH = r.hX < 0 || r.hX >= COLS || r.hY < 0 || r.hY >= ROWS
                      || r.hTrail.has(cellKey(r.hX, r.hY)) || r.gTrail.has(cellKey(r.hX, r.hY));
            const hitG = r.gX < 0 || r.gX >= COLS || r.gY < 0 || r.gY >= ROWS
                      || r.gTrail.has(cellKey(r.gX, r.gY)) || r.hTrail.has(cellKey(r.gX, r.gY));
            // Head-to-head → draw
            if (r.hX === r.gX && r.hY === r.gY) { r.hAlive = false; r.gAlive = false; r.winner = "DRAW"; }
            else {
              if (hitH) r.hAlive = false;
              if (hitG) r.gAlive = false;
              if (!r.hAlive && !r.gAlive) r.winner = "DRAW";
              else if (!r.hAlive) r.winner = "R";
              else if (!r.gAlive) r.winner = "L";
            }
            // Award round if resolved
            if (r.winner && !roundResolvedRef.current) {
              roundResolvedRef.current = true;
              if (r.winner === "L") scoreRef.current.l += 1;
              else if (r.winner === "R") scoreRef.current.r += 1;
              const newScore = { ...scoreRef.current };
              setScore(newScore);
              let matchWinner = null;
              if (newScore.l >= ROUNDS_TO_WIN) matchWinner = "L";
              else if (newScore.r >= ROUNDS_TO_WIN) matchWinner = "R";
              if (matchWinner) setMatchOver(matchWinner);
              else {
                // Delay a moment, then start next round
                setTimeout(() => { if (!matchOver) startNextRound(); }, 1200);
              }
            }
          }

          // Host: broadcast snapshot to guest + spectators every tick
          if (mode === "host") {
            const r2 = roundRef.current;
            socket.send({
              type: "state",
              t: r2.tick,
              hX: r2.hX, hY: r2.hY, hAlive: r2.hAlive,
              gX: r2.gX, gY: r2.gY, gAlive: r2.gAlive,
              // Trails as arrays (JSON-safe) — small: at most ~COLS*ROWS entries
              hT: Array.from(r2.hTrail),
              gT: Array.from(r2.gTrail),
              w: r2.winner,
              ls: scoreRef.current.l, rs: scoreRef.current.r,
              matchWinner: matchOver,
            });
          }
        }
      } else if (mode === "guest" || mode === "spectator") {
        // Apply latest snapshot to local render state (once per frame)
        const s = guestSnapRef.current;
        if (s) {
          const r = roundRef.current;
          r.hX = s.hX; r.hY = s.hY; r.hAlive = !!s.hAlive;
          r.gX = s.gX; r.gY = s.gY; r.gAlive = !!s.gAlive;
          r.hTrail = new Set(s.hT || []);
          r.gTrail = new Set(s.gT || []);
          r.winner = s.w || null;
        }
      }

      // ----- Render -----
      ctx.fillStyle = "#050508";
      ctx.fillRect(0, 0, COLS * CELL, ROWS * CELL);
      // Grid dots (subtle)
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      for (let x = 0; x < COLS; x += 4) {
        for (let y = 0; y < ROWS; y += 4) {
          ctx.fillRect(x * CELL, y * CELL, 1, 1);
        }
      }
      // Arena border
      ctx.strokeStyle = "rgba(255,255,255,0.10)";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, COLS * CELL - 1, ROWS * CELL - 1);

      const r = roundRef.current;
      // Trails
      ctx.fillStyle = "#00E5FF";
      ctx.shadowColor = "#00E5FF"; ctx.shadowBlur = 8;
      r.hTrail.forEach((k) => {
        const x = k % COLS, y = Math.floor(k / COLS);
        ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
      });
      ctx.fillStyle = "#FF1053";
      ctx.shadowColor = "#FF1053"; ctx.shadowBlur = 8;
      r.gTrail.forEach((k) => {
        const x = k % COLS, y = Math.floor(k / COLS);
        ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
      });
      // Heads (brighter)
      ctx.shadowBlur = 18;
      if (r.hAlive) {
        ctx.fillStyle = "#B8F5FF";
        ctx.shadowColor = "#00E5FF";
        ctx.fillRect(r.hX * CELL, r.hY * CELL, CELL, CELL);
      }
      if (r.gAlive) {
        ctx.fillStyle = "#FFB8CE";
        ctx.shadowColor = "#FF1053";
        ctx.fillRect(r.gX * CELL, r.gY * CELL, CELL, CELL);
      }
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [stage, mode, matchOver, socket]);

  // ---------------- UI ----------------
  if (stage === "start") {
    return (
      <div className="max-w-md mx-auto" data-testid="nc-start">
        <h2 className="sc-heading text-2xl font-black mb-4">Choose your mode</h2>
        <div className="grid gap-3">
          <button
            data-testid="nc-mode-local"
            onClick={startLocal}
            className="sc-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Hot Seat</div>
            <div className="sc-heading text-xl font-bold mt-1">Play locally</div>
            <div className="text-xs text-zinc-500 mt-1 sc-mono">Both cycles on this keyboard · WASD vs Arrows</div>
          </button>
          <button
            data-testid="nc-mode-online"
            onClick={startOnline}
            className="sc-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Online</div>
            <div className="sc-heading text-xl font-bold mt-1 flex items-center gap-2"><Users className="w-5 h-5" /> Invite a friend</div>
            <div className="text-xs text-zinc-500 mt-1 sc-mono">You get cyan · friend gets pink · viewers can spectate</div>
          </button>
        </div>
        {error && <div className="text-xs text-[color:var(--sc-pink)] mt-3 sc-mono" data-testid="nc-error">{error}</div>}
      </div>
    );
  }

  if (stage === "waiting") {
    const inviteLink = roomId ? `${window.location.origin}/games?join_room=${roomId}&slug=tron` : "";
    const spectateLink = roomId ? `${window.location.origin}/games?join_room=${roomId}&slug=tron&spectate=1` : "";
    return (
      <div className="max-w-md mx-auto text-center" data-testid="nc-waiting">
        <div className="sc-label" style={{ color: "var(--sc-pink)" }}>{mode === "host" ? "Waiting for opponent" : "Joining room…"}</div>
        <h2 className="sc-heading text-2xl font-black mt-1 mb-3">
          {mode === "host" ? "Send them an invite" : "Connecting"}
        </h2>
        <div className="sc-card p-5 space-y-3">
          {mode === "host" && (
            <>
              <button
                data-testid="nc-open-invite"
                onClick={() => setInviteOpen(true)}
                className="sc-btn w-full text-sm"
              >
                Invite a friend
              </button>
              <div className="text-[10px] sc-mono tracking-widest uppercase text-zinc-500">— or share these links —</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500 w-14 text-left sc-mono">Play</span>
                  <input readOnly value={inviteLink} className="sc-input flex-1 text-xs" data-testid="nc-invite-link" />
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(inviteLink); } catch { /* noop */ } }}
                    className="sc-btn-ghost text-xs flex items-center gap-1"
                  ><Copy className="w-3 h-3" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500 w-14 text-left sc-mono flex items-center gap-1"><Eye className="w-3 h-3" />Watch</span>
                  <input readOnly value={spectateLink} className="sc-input flex-1 text-xs" data-testid="nc-spectate-link" />
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(spectateLink); } catch { /* noop */ } }}
                    className="sc-btn-ghost text-xs flex items-center gap-1"
                  ><Copy className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="text-xs text-zinc-500 sc-mono flex items-center gap-1.5 justify-center pt-1">
                <Wifi className={`w-3.5 h-3.5 ${socket.connected ? "text-[color:var(--sc-volt)]" : "text-zinc-600"}`} />
                {socket.connected ? "Room ready" : "Opening room…"}
              </div>
            </>
          )}
          {mode !== "host" && (
            <div className="py-4">
              <div className="text-sm text-zinc-300">
                {socket.connected ? "Joined! Starting…" : "Connecting…"}
              </div>
              {error && <div className="text-xs text-[color:var(--sc-pink)] mt-2 sc-mono">{error}</div>}
            </div>
          )}
        </div>
        <button onClick={cancelOnline} className="sc-btn-ghost text-xs mt-4 inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Cancel
        </button>
        <GameInviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          roomId={roomId}
          gameName="Neon Cycles"
        />
      </div>
    );
  }

  // stage === "playing"
  const myLabel = mode === "local" ? "P1 · WASD"
                : mode === "spectator" ? `@${socket.hello?.host || "host"}`
                : (mode === "host" ? "You · WASD" : `@${socket.hello?.host || "host"}`);
  const opLabel = mode === "local" ? "P2 · Arrows"
                : mode === "spectator" ? `@${socket.hello?.guest || "guest"}`
                : (mode === "host" ? `@${peerName || socket.hello?.guest || "opponent"}` : "You · WASD");

  return (
    <div data-testid="game-neon-cycles" className="flex flex-col items-center gap-3">
      <div className="w-full max-w-[800px] flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="sc-mono text-xs tracking-widest uppercase text-[color:var(--sc-cyan)]">{myLabel}</span>
          <span className="text-2xl font-black tabular-nums text-[color:var(--sc-cyan)]" data-testid="nc-score-left">{score.l}</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="sc-mono text-[10px] tracking-widest uppercase text-zinc-500">FIRST TO {ROUNDS_TO_WIN}</div>
          {mode !== "local" && (
            <div className="sc-mono text-[9px] tracking-widest uppercase mt-0.5 flex items-center gap-2">
              <span className="flex items-center gap-1">
                <Wifi className={`w-3 h-3 ${socket.connected ? "text-[color:var(--sc-volt)]" : "text-[color:var(--sc-pink)]"}`} />
                <span className={socket.connected ? "text-[color:var(--sc-volt)]" : "text-[color:var(--sc-pink)]"}>
                  {socket.connected ? "LIVE" : "OFFLINE"}
                </span>
              </span>
              {specCount > 0 && (
                <span className="flex items-center gap-1 text-zinc-400"><Eye className="w-3 h-3" />{specCount}</span>
              )}
              {mode === "spectator" && (
                <span className="text-[color:var(--sc-cyan)]">SPECTATING</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black tabular-nums text-[color:var(--sc-pink)]" data-testid="nc-score-right">{score.r}</span>
          <span className="sc-mono text-xs tracking-widest uppercase text-[color:var(--sc-pink)]">{opLabel}</span>
        </div>
      </div>

      <div className="relative w-full max-w-[800px] aspect-[5/3] border border-white/10 rounded-sm overflow-hidden bg-black">
        <canvas ref={canvasRef} width={COLS * CELL} height={ROWS * CELL} className="w-full h-full block" data-testid="nc-canvas" />
        {(matchOver || error) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="text-center">
              {error ? (
                <>
                  <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Disconnected</div>
                  <div className="sc-heading text-2xl font-bold mt-2">{error}</div>
                  <button onClick={cancelOnline} className="sc-btn mt-4 text-sm">Back to lobby</button>
                </>
              ) : (
                <>
                  <div className="sc-label mb-2" style={{ color: matchOver === "L" ? "var(--sc-cyan)" : "var(--sc-pink)" }}>Match Over</div>
                  <div className="sc-heading text-4xl font-black" data-testid="nc-winner">
                    {mode === "local"
                      ? (matchOver === "L" ? "Player 1 wins!" : "Player 2 wins!")
                      : (matchOver === "L"
                          ? (mode === "host" ? "You win!" : `@${peerName || "host"} wins!`)
                          : (mode === "host" ? `@${peerName || "guest"} wins!` : "You win!"))}
                  </div>
                  {(mode === "local" || mode === "host") && (
                    <button onClick={restartMatch} className="sc-btn mt-4 text-sm" data-testid="nc-restart">Play again</button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 sc-mono">
        {mode === "spectator"
          ? "Watching · you don't control a cycle"
          : mode === "local"
              ? "Both cycles on the same keyboard · you can't reverse into yourself"
              : `You are the ${mode === "host" ? "host (cyan)" : "guest (pink)"} · you can't reverse into yourself`}
      </div>
    </div>
  );
}
